import type { CSSProperties } from 'react';
import { Wordmark } from '@tmr/design-system';
import { motions } from './backgrounds';
import type { Portal } from './types';
import { getLoginKitMessages } from './i18n';
import type { Locale } from './i18n';

export interface BrandCanvasProps {
  portal: Portal;
  /** Liga/desliga as animações de assinatura. */
  motionOn?: boolean;
  /** Idioma da interface (pt-BR, en, es). */
  locale?: Locale;
}

/**
 * Painel esquerdo completo da marca: base + textura + movimento de assinatura +
 * wordmark + palavra-chave. Lê `--accent`/`--accent-soft` do portal.
 */
export function BrandCanvas({ portal, motionOn = true, locale = 'pt-BR' }: BrandCanvasProps) {
  const Motion = motions[portal.motion] ?? motions.breathe;
  const t = getLoginKitMessages(locale);

  const canvasStyle = {
    '--accent': portal.accent,
    '--accent-soft': portal.accentSoft,
    background: `radial-gradient(140% 120% at 18% 12%, ${portal.base} 0%, ${portal.baseDeep} 78%)`,
  } as CSSProperties;

  return (
    <div className="brand-canvas" style={canvasStyle}>
      {/* grade "+" permanente de baixa opacidade — o motivo da marca, em todo portal */}
      <div className="canvas-texture" aria-hidden="true" />

      {/* movimento de assinatura por portal */}
      <div className={`canvas-motion ${motionOn ? '' : 'is-paused'}`} key={portal.id}>
        <Motion />
      </div>

      {/* fade da borda superior para legibilidade */}
      <div className="canvas-veil" aria-hidden="true" />

      {/* conteúdo */}
      <div className="brand-content">
        <div className="brand-top">
          <Wordmark size={30} color="var(--cream-50)" />
        </div>

        <div className="brand-mid" key={portal.id}>
          <div className="brand-eyebrow">{portal.eyebrow}</div>
          <h1 className="brand-keyword" key={portal.keyword}>
            {portal.keyword}
          </h1>
          <p className="brand-tagline">
            {portal.tagline.map((line, i) => (
              <span key={i}>
                {line}
                {i < portal.tagline.length - 1 ? <br /> : null}
              </span>
            ))}
          </p>
          <p className="brand-sub">{portal.sub}</p>
        </div>

        <div className="brand-foot">
          <p className="foot-phrase">{t.brand.footPhrase}</p>
          <p className="foot-rights">{t.brand.rights}</p>
        </div>
      </div>
    </div>
  );
}
